import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { startOfDay, subDays } from 'date-fns'
import { MY_TASKS_KEY, type ActivityFeedItem, type Project, type User } from '@witty-board/shared'
import { useRecentActivityQuery } from '../../api/activity.js'
import { useProjectsQuery } from '../../api/projects.js'
import { useUsersQuery } from '../../api/users.js'
import { Avatar } from '../../components/Avatar.js'
import { describeFeedEntry, type ActivityContext } from '../../lib/activity.js'
import { taskPanelPath } from '../../lib/board-routes.js'
import { formatDateTime, formatRelativeTime } from '../../lib/datetime.js'

/** Hasta dónde mira atrás la actividad reciente; "ver más" pagina dentro de esta ventana. */
const RECENT_DAYS = 30

/**
 * Actividad reciente del Inicio (CLAUDE.md §6, fase 7): lo último que se ha
 * creado, cerrado, movido, reasignado, comentado, borrado o restaurado en
 * todos los proyectos, y quién. Registro de solo lectura: sin leído/no leído.
 */
export function RecentActivity() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  // Redondeado al día: forma parte de la clave de la consulta.
  const since = useMemo(() => startOfDay(subDays(new Date(), RECENT_DAYS - 1)).toISOString(), [])
  const feed = useRecentActivityQuery(since)
  const users = useUsersQuery()
  const projects = useProjectsQuery()

  const usersById = useMemo(
    () => new Map((users.data ?? []).map((user) => [user.id, user] as const)),
    [users.data],
  )
  const projectsById = useMemo(
    () => new Map((projects.data ?? []).map((project) => [project.id, project] as const)),
    [projects.data],
  )
  const ctx: ActivityContext = { t, users: usersById, projects: projectsById, locale: i18n.language }
  const entries = feed.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <section aria-labelledby="recent-activity-title" className="flex flex-col gap-2">
      <h2 id="recent-activity-title" className="text-body font-medium text-text">
        {t('home.activity.title')}
      </h2>

      <div className="max-h-60 overflow-y-auto rounded-panel bg-surface p-1">
        {feed.isPending &&
          [0, 1, 2, 3].map((index) => (
            <div
              key={index}
              aria-hidden="true"
              className="m-1 h-8 animate-pulse rounded-card bg-surface-hi"
            />
          ))}

        {feed.isError && (
          <p role="alert" className="px-3 py-3 text-meta text-danger">
            {t('home.activity.loadError')}
          </p>
        )}

        {feed.isSuccess && entries.length === 0 && (
          <p className="px-3 py-3 text-meta text-text-muted">{t('home.activity.empty')}</p>
        )}

        {entries.length > 0 && (
          <ul className="flex flex-col">
            {entries.map((entry) => (
              <li key={entry.id}>
                <ActivityRow
                  entry={entry}
                  actor={usersById.get(entry.actorId)}
                  project={projectsById.get(entry.task.projectId)}
                  description={describeFeedEntry(entry, ctx)}
                  locale={i18n.language}
                  // Sobre el propio Inicio, igual que una tarjeta de Mis tareas:
                  // abrir una tarea no cambia el fondo.
                  onOpen={() =>
                    void navigate({
                      pathname: taskPanelPath(MY_TASKS_KEY, entry.task.id),
                      search: location.search,
                    })
                  }
                />
              </li>
            ))}
          </ul>
        )}

        {feed.hasNextPage && (
          <button
            type="button"
            onClick={() => void feed.fetchNextPage()}
            disabled={feed.isFetchingNextPage}
            className="flex h-10 w-full items-center rounded-card px-3 text-left text-meta text-text-muted hover:bg-surface-hi hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60 max-md:h-11"
          >
            {feed.isFetchingNextPage ? t('common.loading') : t('home.activity.more')}
          </button>
        )}
      </div>
    </section>
  )
}

interface ActivityRowProps {
  entry: ActivityFeedItem
  actor: User | undefined
  project: Project | undefined
  description: string
  locale: string
  onOpen: () => void
}

/** Fila de 40px (CLAUDE.md §6): quién, qué, clave y título de la tarea, extracto si es un comentario, y cuándo. */
function ActivityRow({ entry, actor, project, description, locale, onOpen }: ActivityRowProps) {
  const { t } = useTranslation()
  const deleted = entry.task.deletedAt !== null

  // La frase entera es un único bloque que se trunca: con piezas flex
  // separadas, en 375px el nombre y el verbo se comían el título. La hora
  // queda fuera del bloque para verse siempre.
  const content = (
    <>
      {actor && <Avatar name={actor.name} avatarUrl={actor.avatarUrl} />}
      <span className="min-w-0 flex-1 truncate">
        {/* En móvil basta el avatar: el nombre se queda para lectores de pantalla. */}
        <span className="font-medium text-text max-md:sr-only">{actor?.name ?? '—'}</span>{' '}
        {description} {project && <span className="font-medium">{project.key}</span>}{' '}
        <span className={deleted ? 'line-through' : 'text-text'}>{entry.task.title}</span>
        {deleted && <span className="sr-only">{t('home.activity.deletedTask')}</span>}
        {entry.commentExcerpt && (
          <> {t('home.activity.excerpt', { text: entry.commentExcerpt })}</>
        )}
      </span>
      <time
        dateTime={entry.createdAt}
        title={formatDateTime(entry.createdAt, locale)}
        className="shrink-0 pl-2"
      >
        {formatRelativeTime(entry.createdAt, locale)}
      </time>
    </>
  )

  const classes =
    'flex h-10 w-full min-w-0 items-center gap-2 whitespace-nowrap rounded-card px-3 text-left text-meta text-text-muted max-md:h-11'

  // Una tarea borrada no tiene panel que abrir: se ve en la papelera.
  if (deleted) return <div className={classes}>{content}</div>

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`${classes} hover:bg-surface-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent`}
    >
      {content}
    </button>
  )
}
